import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';

/**
 * Paywall (bloqueio DURO) por atraso de pagamento.
 *
 * Quando a assinatura atinge `blockLevel === 'hard'` (>= N dias de atraso,
 * configurável no /admin), cobre a tela inteira com um overlay que só deixa
 * regularizar (ou sair). Bloqueia TODOS os papéis — a clínica inteira para.
 * Só owner/manager veem o CTA de pagamento; os demais veem para chamar o
 * responsável.
 *
 * A régua de verdade é o backend (SubscriptionBlockGuard barra as escritas);
 * este overlay é a camada visível. Leituras seguem abertas por baixo, mas o
 * overlay impede a operação.
 */
interface SubStatus {
  status: string;
  blockLevel: 'none' | 'soft' | 'hard';
  daysOverdue: number | null;
}

export function PaywallOverlay() {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isE2E =
    typeof navigator !== 'undefined' && navigator.webdriver === true;

  // NÃO cobrir a própria página de assinatura — senão o CTA "Regularizar
  // pagamento" leva a uma tela que o overlay bloqueia (o pagamento fica
  // inalcançável). A régua real é o backend (SubscriptionBlockGuard barra as
  // escritas); aqui só liberamos a VISÃO da tela onde a clínica regulariza.
  const onBillingScreen =
    location.pathname.startsWith('/configuracoes') &&
    searchParams.get('section') === 'assinatura';

  const { data } = useQuery<SubStatus>({
    queryKey: ['subscription.status'],
    queryFn: async () => (await api.get('/me/subscription/status')).data,
    staleTime: 60_000,
    refetchOnWindowFocus: !isE2E,
    refetchInterval: isE2E ? false : 5 * 60_000,
    retry: 1,
    enabled: !isE2E,
  });

  if (isE2E || !data || data.blockLevel !== 'hard') return null;
  if (onBillingScreen) return null;

  const canPay = user?.role === 'owner' || user?.role === 'manager';
  const overdue = data.daysOverdue ?? 0;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-ink-900/70 backdrop-blur-sm p-4"
      role="alertdialog"
      aria-modal="true"
      data-testid="paywall-overlay"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-ink-200 p-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-rose-100">
          <svg
            className="h-7 w-7 text-rose-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m0 0v-2m0 2a9 9 0 100-18 9 9 0 000 18zm0-13v6"
            />
          </svg>
        </div>

        <h2 className="text-xl font-semibold text-ink-900">
          Acesso pausado por pagamento em atraso
        </h2>

        <p className="mt-2 text-sm text-ink-600">
          A assinatura do Kevins Clínicas está vencida
          {overdue > 0 ? ` há ${overdue} dias` : ''}. Para voltar a usar a agenda,
          faturas e planos, é preciso regularizar o pagamento.
        </p>

        <p className="mt-2 text-xs text-ink-500">
          Seus dados continuam seguros e podem ser consultados — nada foi apagado.
        </p>

        {canPay ? (
          <Link
            to="/configuracoes?section=assinatura"
            className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rose-700"
            data-testid="paywall-cta"
          >
            Regularizar pagamento
          </Link>
        ) : (
          <div className="mt-5 rounded-lg bg-ink-50 border border-ink-200 px-4 py-3 text-sm text-ink-600">
            Peça ao responsável da clínica (owner/gerente) para regularizar a
            assinatura.
          </div>
        )}

        <p className="mt-4 text-xs text-ink-400">
          Já pagou? Pode levar até um dia útil para registrarmos. Se persistir,
          fale com o suporte.
        </p>
      </div>
    </div>
  );
}
